/**
 * Procedural texture atlas.
 *
 * Textures are generated in code rather than loaded from PNGs so the project
 * has zero binary assets to start with - easier to version, and you can tune
 * a block's look by changing numbers instead of opening an image editor.
 * Hand-drawn textures can replace this any time; the mesher won't care.
 */

import * as THREE from "three";
import { ATLAS_TILES, TILE_PX, TILE_COUNT } from "../core/block/tiles.js";
import { paintTile, packTextureArray, TILES } from "../core/block/tilePixels.js";

const ATLAS_PX = ATLAS_TILES * TILE_PX;

/** Built once and shared - the UI draws its block icons from the same pixels. */
let atlasCanvas: HTMLCanvasElement | null = null;

/**
 * The atlas as a data URL, built once.
 *
 * Two panels draw icons from it now, and toDataURL on a canvas this size is
 * not something to do twice for the same pixels.
 */
export function atlasDataUrl(): string {
  atlasUrl ??= buildAtlasCanvas().toDataURL();
  return atlasUrl;
}

let atlasUrl: string | null = null;

export function buildAtlasCanvas(): HTMLCanvasElement {
  if (atlasCanvas !== null) return atlasCanvas;

  const canvas = document.createElement("canvas");
  canvas.width = ATLAS_PX;
  canvas.height = ATLAS_PX;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("2D canvas context unavailable");

  const image = ctx.createImageData(ATLAS_PX, ATLAS_PX);
  for (const [tileStr, spec] of Object.entries(TILES)) {
    const tile = Number(tileStr);
    const px = paintTile(tile, spec);
    const tx = (tile % ATLAS_TILES) * TILE_PX;
    const ty = Math.floor(tile / ATLAS_TILES) * TILE_PX;
    for (let y = 0; y < TILE_PX; y++) {
      for (let x = 0; x < TILE_PX; x++) {
        const si = (y * TILE_PX + x) * 4;
        const di = ((ty + y) * ATLAS_PX + (tx + x)) * 4;
        image.data[di] = px[si]!;
        image.data[di + 1] = px[si + 1]!;
        image.data[di + 2] = px[si + 2]!;
        image.data[di + 3] = 255;
      }
    }
  }
  ctx.putImageData(image, 0, 0);

  atlasCanvas = canvas;
  return canvas;
}

/**
 * The same tiles, with their transparency kept.
 *
 * `buildAtlasCanvas` writes every pixel opaque, which is right for the
 * interface: an icon sits in a slot with a background of its own and a
 * see-through one would show the panel through the middle of a шабля.
 *
 * It is wrong for anything held. A tool tile is drawn as `alpha: 0` plus a
 * shape - the background is *meant* to be nothing - so forcing it opaque
 * turns a sabre into a black square with a sabre painted on it, held up in
 * front of you like a card. That is what "a picture out of the inventory"
 * was: literally the inventory's picture, backing and all.
 *
 * A second canvas rather than making the first one see-through, because the
 * two consumers genuinely want different pixels.
 */
let heldCanvas: HTMLCanvasElement | null = null;

export function buildHeldCanvas(): HTMLCanvasElement {
  if (heldCanvas !== null) return heldCanvas;

  const canvas = document.createElement("canvas");
  canvas.width = ATLAS_PX;
  canvas.height = ATLAS_PX;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("2D canvas context unavailable");

  const image = ctx.createImageData(ATLAS_PX, ATLAS_PX);
  for (const [tileStr, spec] of Object.entries(TILES)) {
    const tile = Number(tileStr);
    const px = paintTile(tile, spec);
    const tx = (tile % ATLAS_TILES) * TILE_PX;
    const ty = Math.floor(tile / ATLAS_TILES) * TILE_PX;
    for (let y = 0; y < TILE_PX; y++) {
      for (let x = 0; x < TILE_PX; x++) {
        const si = (y * TILE_PX + x) * 4;
        const di = ((ty + y) * ATLAS_PX + (tx + x)) * 4;
        image.data[di] = px[si]!;
        image.data[di + 1] = px[si + 1]!;
        image.data[di + 2] = px[si + 2]!;
        // The one line that differs from the sheet above.
        image.data[di + 3] = px[si + 3]!;
      }
    }
  }
  ctx.putImageData(image, 0, 0);

  heldCanvas = canvas;
  return canvas;
}

/**
 * The block textures as a texture array - one layer per tile.
 *
 * This is what makes greedy meshing possible. A merged quad spanning eight
 * blocks has UVs running 0..8 and relies on the texture repeating; with an
 * atlas that repeat would wander into whatever tile sits alongside. Each tile
 * being its own layer means it can wrap cleanly, and mipmaps stay usable
 * because no two textures share an edge.
 */
export function buildBlockTextureArray(): THREE.DataArrayTexture {
  // Row order is handled by packTextureArray - see the note there. The 2D
  // sheet above is *not* flipped, because a canvas draws top-down.
  // Copied into a plain ArrayBuffer-backed view: Three.js types the image data
  // as ArrayBuffer, and a Uint8Array is only structurally compatible.
  const packed = new Uint8Array(packTextureArray());
  const texture = new THREE.DataArrayTexture(packed, TILE_PX, TILE_PX, TILE_COUNT);
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  // Crisp pixel-art look, and the wrap that merged quads depend on.
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestMipmapLinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}
